import type { RecoilState } from 'recoil';
import { useRecoilState } from 'recoil';
import type React from 'react';
import { useCallback, useEffect, useState } from 'react';
import type { AnalysisSubject } from 'venn-utils';
import { getWorkspaceDefaultFee } from 'venn-utils';
import type { SubjectWithOptionalFee } from '../../types';
import type { WorkspaceFeeLimits } from '../advisoryFeeConfig';

export const useNameStateEditor = (nameState: RecoilState<string>) => {
  const [name, setName] = useRecoilState(nameState);
  const [tempName, setTempName] = useState(name);
  useEffect(() => setTempName(name), [name]);

  const onNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => setTempName(e.target.value), []);
  const onNameApply = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      // If the new name is valid, use it, otherwise reset the temp name back to the valid current name.
      return e.target.value ? setName(e.target.value) : setTempName(name);
    },
    [name, setName],
  );

  return {
    name: tempName,
    onNameChange,
    onNameApply,
  };
};

export const getSubjectAdvisoryFeeMapping = ({
  subject,
  analysisSubject,
  feeLimits,
  hasDefaultRLAdvisoryFeesFF,
  hasAdvisoryFeesForRlInvestmentsFF,
  isReportLab,
}: {
  subject: SubjectWithOptionalFee;
  analysisSubject: AnalysisSubject;
  feeLimits: WorkspaceFeeLimits | undefined;
  hasDefaultRLAdvisoryFeesFF: boolean;
  hasAdvisoryFeesForRlInvestmentsFF: boolean;
  isReportLab: boolean;
}): Record<string, number> | undefined => {
  const defaultFee = getWorkspaceDefaultFee(hasDefaultRLAdvisoryFeesFF ? feeLimits : undefined);
  if (
    !isReportLab ||
    analysisSubject.isHistoricalPortfolio ||
    analysisSubject.private ||
    defaultFee === undefined ||
    (analysisSubject.fund && !hasAdvisoryFeesForRlInvestmentsFF)
  ) {
    return undefined;
  }
  const { feesMapping } = subject;
  if (feesMapping && Object.keys(feesMapping).length > 0) {
    return feesMapping;
  }
  return { [String(analysisSubject.id)]: defaultFee };
};
